import Button from "@/components/Button"
import { useGame } from "@/hooks/useGame"
import { useGameMoves } from "@/hooks/useGameMoves"
import { useModals } from "@/hooks/useModals"
import {
  atLastLine,
  getChildLines,
  getParentMovePosition,
  incrementLinePosition,
} from "@/utils/moveHelpers"
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline"
import clsx from "clsx"
import { useEffect } from "react"

const BoardControls = () => {
  const { activeModal } = useModals()
  const modalOpen = activeModal !== null

  const { orientation, toggleOrientation } = useGame()
  const {
    hasNextMove,
    hasPreviousMove,
    previousMove,
    nextMove,
    activeMove,
    goToMove,
    movesHistory,
  } = useGameMoves()

  useEffect(() => {
    const getChildPosition = () => {
      // Try the first child line
      const childLines = getChildLines(movesHistory, activeMove)
      if (childLines.length > 0) {
        return activeMove.concat([[0, 0]])
      }

      // Try the next sibling line
      if (!atLastLine(movesHistory, activeMove)) {
        return incrementLinePosition(activeMove)
      }
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) return
      if (modalOpen) return

      switch (e.key) {
        case "ArrowLeft":
          previousMove()
          break
        case "ArrowUp":
          const pos = getParentMovePosition(activeMove)
          if (Boolean(pos?.length)) goToMove(pos)
          break
        case "ArrowRight":
          e.preventDefault()
          nextMove()
          break
        case "ArrowDown":
          const childPos = getChildPosition()
          if (childPos) goToMove(childPos)
          break
      }
    }

    window.addEventListener("keydown", handleKeyDown)

    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [nextMove, previousMove, activeMove, goToMove, movesHistory, modalOpen])

  return (
    <div className="flex items-center justify-between py-4">
      <Button style="secondary" size="lg" onClick={toggleOrientation}>
        <span className="sr-only">Toggle board orientation</span>
        <div className="relative h-5 w-5 rounded border border-gray-900">
          <div
            className={clsx("absolute inset-x-0 h-1/2 bg-gray-900", {
              "bottom-0": orientation === "white",
              "top-0": orientation === "black",
            })}
          />
        </div>
      </Button>

      <div className="flex gap-x-2">
        <Button
          style="secondary"
          size="lg"
          onClick={previousMove}
          disabled={!hasPreviousMove}
        >
          <span className="sr-only">Previous move</span>
          <ChevronLeftIcon className="h-5 w-5" />
        </Button>
        <Button
          style="secondary"
          size="lg"
          onClick={nextMove}
          disabled={!hasNextMove}
        >
          <span className="sr-only">Next move</span>
          <ChevronRightIcon className="h-5 w-5" />
        </Button>
      </div>
    </div>
  )
}

export default BoardControls
